import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Modal, Form, Select, DatePicker, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useFinanzasEmpresaLogic } from "../finanzas-empresa/useFinanzasEmpresaLogic";
import { ArriendosSection } from "../finanzas-empresa/sectionArriendos";
import {
  FiltroGlobalFecha,
  FiltroFecha,
  useFiltroFechaPersistido,
} from "../finanzas-empresa/filtroGlobalFecha";
import CalendarCreateModal, {
  type RangeSelection,
  type CreatePayload,
} from "../calendario/calendarCreateModal";
import RecalcBookingsBulkButton from "../../Components/RecalcBookingsBulkButton";
import { type Cleaner } from "../calendario/useCalendarioLogic";
import { usersApi } from "../../services/users.api";
import { apiFetch } from "../../services/apiClient";
import { useAuth } from "../../context/AuthContext";
import { formatPropertyName } from "../../utils/propertyDisplay";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../utils/antdStatic";
import { ahoraChile } from "../../utils/fechaChile";

// Shape interno con los campos financieros que el `CalendarCreateModal`
// necesita en su `RangeSelection` (autocálculo de comisiones, IVA,
// desgloses de aseo). El endpoint `/propiedades` los expone en snake_case;
// los mapeamos al formato que espera el modal — mismo mapeo que hace
// `useCalendarioLogic.fetchProperties` (round 2026-04-30 nonies).
type PropiedadRica = {
  id: number;
  nombre: string;
  costoAseo: number | null;
  sueldoAseo: number | null;
  tasaComisionAf: number | null;
  transporteNeto: number | null;
  transporteTotal: number | null;
  materialesNeto: number | null;
  materialesTotal: number | null;
  lavanderiaNeto: number | null;
  lavanderiaTotal: number | null;
  hasLavanderiaAf: boolean;
};

const safeNumber = (v: any): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const { Option } = Select;

/**
 * Página dedicada de Arriendos — tabla + filtros canónicos + creación
 * inline.
 *
 * Round 2026-04-30 nonies — antes el botón "Crear arriendo" navegaba a
 * `/calendario` para que el usuario usara ahí el `CalendarCreateModal`.
 * El usuario pidió expresamente que la creación ocurra IN-PLACE en
 * /arriendos sin sacarlo de la página, aunque el modal sea idéntico al
 * del calendario. Lo embebemos directamente.
 *
 * Lo que NO embebemos: el grid del calendario (`useCalendarioLogic`
 * carga bookings/cleanings/blocks/precios — pesado e innecesario para
 * esta vista). En su lugar, replicamos solo lo mínimo:
 *  - `propiedades` viene de `useFinanzasEmpresaLogic` (cache compartido).
 *  - `aseadores` se carga aquí con `usersApi.getCleaners()`.
 *  - `onCreate` es un POST directo a `/stays/calendario` (mismo endpoint
 *    que usa calendario.tsx).
 *  - `updateEvent` / `deleteEvent` son stubs: el modal solo se abre en
 *    `mode="create"` desde acá, esos handlers nunca se invocan.
 *  - Tras un create exitoso bumpeamos `refreshKey` para que
 *    `<ArriendosSection>` se remonte y vuelva a fetchear su tabla
 *    (no expone método `refresh()` propio).
 *
 * Restricciones aplicadas:
 *  - `allowedKinds=["booking"]`: solo arriendo, no aseo / bloqueo /
 *    mantención (esos tienen sus propios módulos).
 */
export default function ArriendosPage() {
  const { user } = useAuth();
  const {
    sidebarOpen,
    toggleSidebar,
    loading: loadingFinanzas,
    propiedades: propiedadesFinanzas,
    arriendos,
  } = useFinanzasEmpresaLogic();

  const [searchParams] = useSearchParams();

  // Si se llega con `from`/`to` en la URL (botón "Ver en finanzas" del
  // calendario), arrancamos el filtro global de fecha en modo "rango"
  // cubriendo esas fechas. Así el arriendo prefiltrado (por `bookingId`,
  // que lee la tabla) no queda fuera del rango por la regla de mes
  // contable. Sin esos params, default al mes actual como siempre.
  // Un enlace directo manda sobre el período guardado: si el admin llega
  // desde una card de /finanzas-empresa o desde el calendario, tiene que ver
  // ESE mes y no el que dejó la última vez.
  const hayEnlaceDirecto = Boolean(
    searchParams.get("mes") ||
      (searchParams.get("from") && searchParams.get("to")),
  );
  const [filtroFecha, setFiltroFecha] = useFiltroFechaPersistido(
    "arriendos.periodo",
    (): FiltroFecha => {
    // Deep-link mensual: `/arriendos?mes=YYYY-MM` (botones "Ver detalle del
    // sector" / "Ver todos los sectores" de /finanzas-empresa). Aterriza
    // limpio en modo MES (no rango) para reflejar el mes contable.
    const mes = searchParams.get("mes");
    if (mes) {
      const m = dayjs(`${mes}-01`);
      if (m.isValid()) return { modo: "mes", mes: m };
    }
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    if (from && to) {
      const f = dayjs(from);
      const t = dayjs(to);
      if (f.isValid() && t.isValid()) {
        return { modo: "rango", rango: [f, t] };
      }
    }
      return { modo: "mes", mes: ahoraChile() };
    },
    { restaurar: !hayEnlaceDirecto },
  );

  // Aseadores para el Select del modal. Cargamos solo una vez al montar.
  const [aseadores, setAseadores] = useState<Cleaner[]>([]);
  const [loadingAseadoras, setLoadingAseadoras] = useState(false);

  // Propiedades enriquecidas con los defaults financieros (tasaComisionAf,
  // costoAseo, etc). `propiedadesFinanzas` solo trae id+nombre — no alcanza
  // para que el modal autocalcule. Mismo endpoint y mapeo que usa el hook
  // del calendario.
  const [propiedadesRicas, setPropiedadesRicas] = useState<PropiedadRica[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingAseadoras(true);
      try {
        const [cleaners, propsResp] = await Promise.all([
          usersApi.getCleaners(),
          // limit=400 (máximo del backend): la lista es catálogo del filtro
          // por propiedad — un default más bajo truncaba en silencio.
          apiFetch<any>("/propiedades?limit=400"),
        ]);
        if (cancelled) return;
        if (cleaners) {
          setAseadores(
            cleaners.map((c: any) => ({
              id: c.id,
              nombre: c.nombre ?? "",
              rut: c.rut ?? "",
              email: c.email ?? "",
              telefono: c.telefono ?? "",
              banco: c.banco ?? "",
              tipocuenta: c.tipocuenta ?? "",
              numerocuenta: c.numerocuenta ?? "",
              rol: c.rol,
              sector: c.sector,
            })),
          );
        }
        if (propsResp) {
          const arr: any[] = Array.isArray(propsResp)
            ? propsResp
            : (propsResp?.data ?? []);
          setPropiedadesRicas(
            arr
              .filter((p) => p.nombrepropiedad && p.habilitado !== false)
              .map((p) => ({
                id: p.id,
                nombre: p.nombrepropiedad,
                costoAseo: safeNumber(p.precio_limpieza_cobrado),
                sueldoAseo: safeNumber(p.precio_limpieza_aseador),
                tasaComisionAf:
                  (safeNumber(p.comision_propiedad) ?? 0) / 100,
                transporteNeto: safeNumber(p.precio_transporte_neto),
                transporteTotal: safeNumber(p.precio_transporte_total),
                materialesNeto: safeNumber(p.precio_materiales_neto),
                materialesTotal: safeNumber(p.precio_materiales_total),
                lavanderiaNeto: safeNumber(p.precio_lavanderia_neto),
                lavanderiaTotal: safeNumber(p.precio_lavanderia_total),
                hasLavanderiaAf: Boolean(p.esLavanderiaInterna),
              })),
          );
        }
      } finally {
        if (!cancelled) setLoadingAseadoras(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Step 1 — modal "starter" donde el usuario elige propiedad + rango
  // de fechas. Necesario porque `RangeSelection` requiere propertyId,
  // startDate y endDate antes de abrir `CalendarCreateModal`. En el
  // calendario esto se resuelve haciendo drag sobre la grilla; acá
  // mostramos un mini-formulario.
  const [starterOpen, setStarterOpen] = useState(false);
  const [starterForm] = Form.useForm();

  // Step 2 — modal real de creación de arriendo.
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createRange, setCreateRange] = useState<RangeSelection | null>(null);

  // Bumper para forzar refetch de ArriendosSection tras crear.
  const [refreshKey, setRefreshKey] = useState(0);

  const handleStarterFinish = (vals: {
    propiedad: number;
    rango: [Dayjs, Dayjs];
  }) => {
    const propId = vals.propiedad;
    const prop = propiedadesRicas.find((p) => p.id === propId);
    const startDate = vals.rango[0].format("YYYY-MM-DD");
    const endDate = vals.rango[1].format("YYYY-MM-DD");

    setCreateRange({
      propertyId: propId,
      propertyName: prop?.nombre ?? null,
      startDate,
      endDate,
      ...(prop?.tasaComisionAf != null
        ? { tasaComisionAf: prop.tasaComisionAf }
        : {}),
      ...(prop?.costoAseo != null ? { costoAseo: prop.costoAseo } : {}),
      ...(prop?.sueldoAseo != null ? { sueldoAseo: prop.sueldoAseo } : {}),
      ...(prop?.transporteNeto != null
        ? { transporteNeto: prop.transporteNeto }
        : {}),
      ...(prop?.transporteTotal != null
        ? { transporteTotal: prop.transporteTotal }
        : {}),
      ...(prop?.materialesNeto != null
        ? { materialesNeto: prop.materialesNeto }
        : {}),
      ...(prop?.materialesTotal != null
        ? { materialesTotal: prop.materialesTotal }
        : {}),
      ...(prop?.lavanderiaNeto != null
        ? { lavanderiaNeto: prop.lavanderiaNeto }
        : {}),
      ...(prop?.lavanderiaTotal != null
        ? { lavanderiaTotal: prop.lavanderiaTotal }
        : {}),
      hasLavanderiaAf: Boolean(prop?.hasLavanderiaAf),
    });
    setStarterOpen(false);
    setIsCreateOpen(true);
  };

  const onCreate = useCallback(async (payload: CreatePayload) => {
    const res = await apiFetch<{ success?: boolean; message?: string }>(
      "/stays/calendario",
      {
        method: "POST",
        body: JSON.stringify(payload),
        successMsg: "Arriendo creado",
      },
    );
    if (!res) return;

    setIsCreateOpen(false);
    setCreateRange(null);
    starterForm.resetFields();
    // Forzamos remount de la tabla — ArriendosSection no expone refresh.
    setRefreshKey((k) => k + 1);
  }, [starterForm]);

  // Stubs requeridos por la interfaz del modal. Nunca se invocan desde
  // /arriendos porque siempre abrimos en mode="create" sin initialValues.
  // Si alguien clickea Eliminar dentro del modal en modo create (no
  // debería poder), devolvemos rejected para evitar accidentes silenciosos.
  const updateEventStub = useCallback(async () => {
    getMsg()?.error("La edición de arriendos se hace desde el calendario.");
  }, []);
  const deleteEventStub = useCallback(async () => {
    getMsg()?.error("La eliminación de arriendos se hace desde el calendario.");
    return false;
  }, []);

  // Rango por defecto para el recálculo masivo: el mismo período que el admin
  // está viendo en la página. El modal del botón lo deja editable, así el
  // scope queda explícito (no heredado en silencio del filtro de la tabla).
  const bulkDefaultRange: [Dayjs, Dayjs] | null =
    filtroFecha.modo === "rango" && filtroFecha.rango
      ? filtroFecha.rango
      : (() => {
          const m = filtroFecha.mes ?? ahoraChile();
          return [m.startOf("month"), m.endOf("month")];
        })();

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div className="finanzas-page">
        <PageHeader
          title="Arriendos"
          helpText="Tabla de arriendos con filtros y exportación. Crear un arriendo desde acá abre el mismo modal del calendario."
        />

        <Space style={{ marginBottom: 16 }} wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              starterForm.resetFields();
              setStarterOpen(true);
            }}
          >
            Crear arriendo
          </Button>
          {/* Recálculo masivo de finanzas — admin-only (el componente filtra
              por rol). Prefilado con el período visible; editable en el modal. */}
          <RecalcBookingsBulkButton
            userRole={user?.rol ?? null}
            defaultRange={bulkDefaultRange}
            onRecalculated={() => setRefreshKey((k) => k + 1)}
          />
        </Space>

        <div style={{ marginBottom: 16 }}>
          <FiltroGlobalFecha
            value={filtroFecha}
            onChange={setFiltroFecha}
            allowTodos
          />
        </div>

        <ArriendosSection
          clavePersistencia="arriendos.tabla"
          // `key` fuerza remount cuando creamos un arriendo nuevo, así
          // ArriendosSection vuelve a ejecutar su fetch interno.
          key={refreshKey}
          data={arriendos}
          loading={loadingFinanzas}
          propiedades={propiedadesFinanzas}
          filtroFecha={filtroFecha}
          // Botón de recálculo por fila — solo en /arriendos, no en
          // /finanzas-empresa (esa vista es de auditoría, no de
          // gestión). El componente RecalcBookingButton ya filtra por
          // rol internamente — admin/superadmin lo ven, los demás no.
          enableRecalcAction
          // Enlace por fila para abrir el arriendo en el modal de edición
          // del calendario (deep-link). Admin-only (gateado adentro).
          enableCalendarEditLink
          userRole={user?.rol ?? null}
          onRecalculated={() => {
            // Tras recalc exitoso, fuerza refetch del listado.
            setRefreshKey((k) => k + 1);
          }}
        />

        {/* Step 1 — selector previo de propiedad + fechas */}
        <Modal
          title="Crear arriendo"
          open={starterOpen}
          onCancel={() => setStarterOpen(false)}
          onOk={() => starterForm.submit()}
          okText="Continuar"
          cancelText="Cancelar"
          destroyOnClose
        >
          <Form
            form={starterForm}
            layout="vertical"
            onFinish={handleStarterFinish}
            initialValues={{
              rango: [ahoraChile(), ahoraChile().add(1, "day")],
            }}
          >
            <Form.Item
              label="Propiedad"
              name="propiedad"
              rules={[{ required: true, message: "Selecciona una propiedad" }]}
            >
              <Select
                showSearch
                placeholder="Buscar propiedad..."
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {propiedadesRicas.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {formatPropertyName(p.nombre)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Rango de fechas"
              name="rango"
              rules={[{ required: true, message: "Selecciona un rango" }]}
            >
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Step 2 — el mismo modal del calendario, embebido aquí */}
        <CalendarCreateModal
          open={isCreateOpen}
          range={createRange}
          mode="create"
          initialValues={null}
          onClose={() => {
            setIsCreateOpen(false);
            setCreateRange(null);
          }}
          onCreate={onCreate}
          aseadores={aseadores}
          loadingAseadoras={loadingAseadoras}
          updateEvent={updateEventStub}
          deleteEvent={deleteEventStub}
          // Restringimos a booking — esta página es para crear arriendos,
          // no aseos / bloqueos / mantenciones (esos viven en sus
          // módulos: /aseos, /calendario, etc.).
          allowedKinds={["booking"]}
        />
      </div>
    </Layout>
  );
}
